/**
 * ProjectContext
 *
 * Centralized project state management with automatic listener cleanup.
 * Extracts projectId from URL and provides project-related state to children.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  ProjectContextValue,
  ProjectProviderProps,
  UserProjectRole,
} from '../../types';
import { extractProjectIdFromPathname, isProjectPage } from '../../constants';
import { ListenerRegistry } from '@modules/firebase-core/services/ListenerRegistry';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

/**
 * Default context value
 */
const defaultContextValue: ProjectContextValue = {
  projectId: null,
  projectName: '',
  userRole: null,
  isLoading: false,
  error: null,
  isProjectPage: false,
  canManageUsers: false,
  canEdit: false,
  refreshProject: async () => {},
  clearProject: () => {},
};

/**
 * Project Context
 */
const ProjectContext = createContext<ProjectContextValue | undefined>(
  undefined
);

/**
 * ProjectProvider Component
 *
 * Provides centralized project state to the application.
 * Automatically cleans up Firestore listeners when project changes.
 *
 * @example
 * ```tsx
 * <ProjectProvider>
 *   <App />
 * </ProjectProvider>
 * ```
 */
export function ProjectProvider({ children }: ProjectProviderProps) {
  const { pathname, search } = useLocation();

  // State
  const [projectName, setProjectName] = useState<string>('');
  const [userRole, setUserRole] = useState<UserProjectRole | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs
  const prevProjectIdRef = useRef<string | null>(null);

  // Extract projectId from URL (pathname or query params)
  const projectId = useMemo(() => {
    // First, try to extract from pathname (e.g., /project/123)
    const pathProjectId = extractProjectIdFromPathname(pathname);
    if (pathProjectId) {
      return pathProjectId;
    }

    // Fallback: check query params (e.g., /users?projectId=123)
    const params = new URLSearchParams(search);
    return params.get('projectId');
  }, [pathname, search]);

  // Derived state
  const isOnProjectPage = useMemo(() => {
    return isProjectPage(pathname);
  }, [pathname]);

  const canManageUsers = useMemo(() => {
    return userRole === 'owner';
  }, [userRole]);

  const canEdit = useMemo(() => {
    return userRole === 'owner' || userRole === 'editor';
  }, [userRole]);

  // Cleanup listeners when project changes
  useEffect(() => {
    const prevProjectId = prevProjectIdRef.current;

    if (prevProjectId && prevProjectId !== projectId) {
      // Project changed - cleanup old project's listeners
      const registry = ListenerRegistry.getInstance();
      registry.cleanupProject(prevProjectId);

      // Reset state for new project
      setProjectName('');
      setUserRole(null);
      setError(null);
    }

    prevProjectIdRef.current = projectId;
  }, [projectId]);

  // Refresh project data
  const refreshProject = useCallback(async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const { CoreFirebaseService } = await import('@modules/firebase-core/services/CoreFirebaseService');
      const db = CoreFirebaseService.getInstance().firestore;

      // Fetch project data
      const projectRef = doc(db, 'projects', projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        throw new Error(`Project ${projectId} not found`);
      }

      const projectData = projectSnap.data();
      setProjectName(projectData.projectName || projectData.name || '');

      // Fetch current user's role in this project
      const { auth } = await import('@frontend/firebase');
      const currentUser = auth.currentUser;

      if (currentUser) {
        const memberRef = doc(db, `projects/${projectId}/members/${currentUser.uid}`);
        const memberSnap = await getDoc(memberRef);

        if (memberSnap.exists()) {
          const memberData = memberSnap.data();
          setUserRole(memberData.role as UserProjectRole);
        } else {
          setUserRole(null);
        }
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error refreshing project:', err);
      setError(err as Error);
      setIsLoading(false);
    }
  }, [projectId]);

  // Load project data when projectId changes
  useEffect(() => {
    if (!projectId) {
      setProjectName('');
      setUserRole(null);
      return;
    }

    // Trigger initial load
    refreshProject();

    // Set up real-time listener for project updates
    const setupListener = async () => {
      const { CoreFirebaseService } = await import('@modules/firebase-core/services/CoreFirebaseService');
      const db = CoreFirebaseService.getInstance().firestore;

      const projectRef = doc(db, 'projects', projectId);

      const unsubscribe = onSnapshot(
        projectRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const projectData = snapshot.data();
            setProjectName(projectData.projectName || projectData.name || '');
          }
        },
        (error) => {
          console.error('Error in project listener:', error);
          setError(error as Error);
        }
      );

      return unsubscribe;
    };

    const listenerPromise = setupListener();

    // Cleanup listener on unmount or projectId change
    return () => {
      listenerPromise.then(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, [projectId, refreshProject]);

  // Clear project state
  const clearProject = useCallback(() => {
    setProjectName('');
    setUserRole(null);
    setError(null);
    setIsLoading(false);
  }, []);

  // Context value
  const contextValue = useMemo<ProjectContextValue>(
    () => ({
      projectId,
      projectName,
      userRole,
      isLoading,
      error,
      isProjectPage: isOnProjectPage,
      canManageUsers,
      canEdit,
      refreshProject,
      clearProject,
    }),
    [
      projectId,
      projectName,
      userRole,
      isLoading,
      error,
      isOnProjectPage,
      canManageUsers,
      canEdit,
      refreshProject,
      clearProject,
    ]
  );

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
}

/**
 * useProject Hook
 *
 * Access the project context.
 *
 * @throws Error if used outside ProjectProvider
 *
 * @example
 * ```tsx
 * function ProjectHeader() {
 *   const { projectId, projectName, userRole, isLoading } = useProject();
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   return <Typography>{projectName}</Typography>;
 * }
 * ```
 */
export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);

  if (context === undefined) {
    throw new Error('useProject must be used within ProjectProvider');
  }

  return context;
}

export { ProjectContext };
